/**
 * Buchungsstrecke B4 (sql/603) — das Begrüßungsvideo des Betriebs auf der neuen Angebotsseite,
 * neben der persönlichen Notiz (Idee Antoine, 29.09.2026: „Domenico bedankt sich für die Anfrage,
 * erzählt kurz über Restaurant und sich, dazu Bilder, ca. 45 Sek.“).
 *
 * Datenschutz: Video, Vorschaubild und Untertitel kommen aus dem eigenen Speicher des Betriebs —
 * kein YouTube/Vimeo, keine Cookies. Nichts wird vorab geladen (`preload="none"`), abgespielt wird
 * erst auf Klick, stumm mit Untertiteln (`muted`, `<track default>`).
 *
 * Messung: beim ERSTEN Abspielen je Seitenaufruf meldet die Seite `video_abgespielt` an den
 * vorhandenen Ereignis-Weg (A5, lib/angebots-ereignis.ts) — nur mit dem Angebots-Token, nie in der
 * Betreiber-Vorschau, ohne IP/Fingerprint. Die Datenbank hält je Vorgang nur das erste Mal.
 */
import { useRef } from "react";
import { Card } from "./components";
import { meldeAngebotsEreignis, type AngebotsEreignisArt } from "../lib/angebots-ereignis";
import type { AngebotsseiteV2 } from "./types";

export const VIDEO_T = {
  de: {
    titel: "Ein Gruß aus dem Haus",
    hinweisUntertitel: "Startet stumm mit Untertiteln. Ton über das Lautsprecher-Symbol.",
    hinweis: "Startet stumm. Ton über das Lautsprecher-Symbol.",
    aria: (betrieb: string | null) => (betrieb ? `Begrüßungsvideo von ${betrieb}` : "Begrüßungsvideo"),
  },
  en: {
    titel: "A welcome from us",
    hinweisUntertitel: "Starts muted with captions. Tap the speaker icon for sound.",
    hinweis: "Starts muted. Tap the speaker icon for sound.",
    aria: (betrieb: string | null) => (betrieb ? `Welcome video from ${betrieb}` : "Welcome video"),
  },
} as const;

export function BegruessungsVideo({
  v2,
  lang,
  token,
  vorschau,
}: {
  v2: AngebotsseiteV2 | null;
  lang: "de" | "en";
  token: string | undefined;
  vorschau: boolean;
}) {
  const gesendet = useRef(new Set<AngebotsEreignisArt>());
  const video = v2?.video;
  if (!video) return null;
  const t = VIDEO_T[lang];
  return (
    <Card className="maestro-druck-aus p-6 md:p-8" data-testid="v2-video">
      <p className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">{t.titel}</p>
      <video
        className="mt-3 block aspect-video w-full rounded-lg bg-black object-contain"
        src={video.videoUrl}
        poster={video.vorschauUrl}
        preload="none"
        controls
        muted
        playsInline
        aria-label={t.aria(video.betrieb)}
        onPlay={() => meldeAngebotsEreignis(token, "video_abgespielt", gesendet.current, vorschau)}
        data-testid="v2-video-player"
      >
        {video.untertitelUrl && <track kind="captions" src={video.untertitelUrl} srcLang="de" label="Deutsch" default />}
      </video>
      <p className="mt-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        {video.untertitelUrl ? t.hinweisUntertitel : t.hinweis}
      </p>
    </Card>
  );
}
